import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InternationalReturnsFlatTeamSettingGroupId =
  | "international_returns.redo_assisted_customs"
  | "international_returns.missing_customs_review"
  | "international_returns.customs_metafields"
  | "international_returns.tax_ids"
  | "international_returns.customs_delay_notifications"
  | "international_returns.customs_csv_import";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "International returns customs data settings used for cross-border return labels.",
};

export const InternationalReturnsFlatTeamSettingGroups = [
  {
    id: "international_returns.redo_assisted_customs",
    title: "Redo assisted customs data",
    description:
      "Redo assisted customs data settings on the International returns page.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: ["redo assisted customs", "international returns customs"],
  },
  {
    id: "international_returns.missing_customs_review",
    title: "Missing customs data review",
    description:
      "Whether international returns missing customs data wait for manual review on the International returns page.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: ["missing customs review", "customs manual review"],
  },
  {
    id: "international_returns.customs_metafields",
    title: "Customs metafields",
    description:
      "Customs metafield settings on the International returns page.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: ["customs metafields", "international returns metafields"],
  },
  {
    id: "international_returns.tax_ids",
    title: "Tax IDs",
    description:
      "Tax identification numbers (EORI, VAT, IOSS) used on customs declarations for cross-border return shipments.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: [
      "tax ids",
      "tax identification numbers",
      "eori number",
      "vat number",
      "ioss number",
    ],
  },
  {
    id: "international_returns.customs_delay_notifications",
    title: "Customs delay notifications",
    description:
      "Status of the email notification sent when a return is held up in customs; the notification itself is managed on the Notifications page.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: [
      "customs delay notifications",
      "customs delay emails",
      "delayed in customs",
      "held in customs",
    ],
  },
  {
    id: "international_returns.customs_csv_import",
    title: "Customs data CSV",
    description:
      "Bulk export and import of product customs data (HS codes, descriptions, origins) via CSV for international return labels.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: [
      "customs csv import",
      "import customs data",
      "customs csv",
      "upload customs data",
    ],
  },
] as const satisfies readonly SettingGroupDefinition<InternationalReturnsFlatTeamSettingGroupId>[];

export const InternationalReturnsFlatTeamSettingGroupById: Record<
  InternationalReturnsFlatTeamSettingGroupId,
  SettingGroupDefinition<InternationalReturnsFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      InternationalReturnsFlatTeamSettingGroupId,
      SettingGroupDefinition<InternationalReturnsFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of InternationalReturnsFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    InternationalReturnsFlatTeamSettingGroupId,
    SettingGroupDefinition<InternationalReturnsFlatTeamSettingGroupId>
  >;
})();
